import React, { useEffect, useRef, useState } from 'react';
import { maybe } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/maybe-util';
import makeStyles from '@mui/styles/makeStyles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import PinIcon from '../../../assets/icons/PinIcon';
import UnpinIcon from '../../../assets/icons/UnpinIcon';
import PinnedIcon from '../../common/PinnedIcon';
import { Resource } from '../../../providers/ResourcesProvider/types';

const MAX_PARAGRAPH_HEIGHT = 90;

const isNull = (value) => !value && typeof value === 'object';

type Props = {
  resource: Resource;
  isPinnedResource: boolean | null;
  pinnedResourceId: number | undefined;
  displayCardHeader: boolean;
  displayPinToggle: boolean;
  onCardClick: (resource: Resource) => void;
  onIconButtonClick: (resource: Resource, pinnedResourceId: number | undefined) => void;
};

type StylesProps = {
  headerThumbnailImage?: string;
};

const ResourceCard: React.FC<Props> = ({
  resource,
  isPinnedResource,
  pinnedResourceId,
  displayCardHeader,
  displayPinToggle,
  onCardClick,
  onIconButtonClick,
}) => {
  const { title, description, thumbnail } = resource;
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [showEllipses, setShowEllipses] = useState(false);
  const classes = useStyles({ headerThumbnailImage: thumbnail });

  const MaybeCardHeader = maybe(() => (
    <CardHeader
      avatar={
        <img
          className={classes.avatar}
          alt={resource.type.title}
          title={resource.type.title}
          src={resource.type.icon}
        />
      }
      action={
        displayPinToggle && (
          <IconButton
            aria-label="pin icon"
            className={`${classes.iconButton} ${isNull(isPinnedResource) ? classes.unpinIcon : ''}`}
            onClick={(event) => {
              onIconButtonClick(resource, pinnedResourceId);
              event.stopPropagation();
            }}
            disableFocusRipple
            disableRipple
          >
            {isNull(isPinnedResource) ? (
              <UnpinIcon className={classes.unpinIcon} />
            ) : isPinnedResource ? (
              <PinnedIcon />
            ) : (
              <PinIcon fillPin={'transparent'} fill={'#000000'} />
            )}
          </IconButton>
        )
      }
      classes={{
        action: classes.cardHeaderAction,
        root: classes.cardHeaderRoot,
      }}
    />
  ));

  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.scrollHeight > MAX_PARAGRAPH_HEIGHT) {
      setShowEllipses(true);
    }
  }, []);

  return (
    <Card
      tabIndex={0}
      component={'button'}
      raised={true}
      className={classes.cardRoot}
      onClick={() => onCardClick(resource)}
    >
      <MaybeCardHeader shouldRender={displayCardHeader} />
      <CardContent className={classes.cardContent}>
        <Typography className={classes.cardContentTitle} variant="h3">
          {title}
        </Typography>
        {thumbnail && <CardMedia className={classes.cardMedia} image={thumbnail} title={title} />}
        <Typography
          className={classes.cardContentDescription}
          variant="body1"
          ref={descriptionRef}
          title={description}
        >
          {description}
        </Typography>
        {description && showEllipses && (
          <Typography
            className={classes.cardContentDescriptionEllipses}
            variant="body1"
            title={description}
          >
            ...
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const useStyles = makeStyles((theme: any) => ({
  avatar: {
    display: 'flex',
    height: remCalc(32),
    justifyContent: 'center',
    marginLeft: remCalc(3),
    width: remCalc(32),
  },
  cardContent: {
    minHeight: remCalc(201),
    '&:last-child': {
      paddingBottom: '0px',
    },
  },
  cardContentTitle: {
    lineHeight: 'normal',
    textOverflow: 'ellipsis',
    marginBottom: remCalc(9),
    maxHeight: remCalc(77),
    width: '100%',
  },
  cardContentDescription: {
    fontSize: remCalc(16),
    maxHeight: '90px',
    minHeight: '75px',
    overflow: 'hidden',
    paddingLeft: ({ headerThumbnailImage }: StylesProps) =>
      `${headerThumbnailImage ? remCalc(20) : '0px'}`,
  },
  cardContentDescriptionEllipses: {
    overflow: 'hidden',
    paddingLeft: ({ headerThumbnailImage }: StylesProps) =>
      `${headerThumbnailImage ? remCalc(20) : '0px'}`,
  },
  cardRoot: {
    '&:hover': {
      cursor: 'pointer',
      transform: 'scale(1.05, 1.05)',
    },
    '&:focus': {
      outline: '#248AFF auto 1px',
      transform: 'scale(1.05, 1.05)',
    },
    overflow: 'visible',
    paddingBottom: '0px',
    border: 'none',
    textAlign: 'left',
    padding: 0,
    width: '100%',
  },
  cardHeaderAction: {
    alignSelf: 'end',
    backgroundColor: 'transparent',
    '&:focus': {
      outline: 'none',
    },
    border: 'none',
    display: 'flex',
    height: remCalc(32),
    justifyContent: 'center',
    marginRight: remCalc(3),
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
    width: remCalc(32),
  },
  cardHeaderRoot: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `${remCalc(1)} solid ${theme.palette.grey[500]}`,
    padding: remCalc(5),
  },
  cardMedia: {
    backgroundPosition: 'top center',
    backgroundSize: 'contain',
    float: 'left',
    height: remCalc(100),
    width: remCalc(100),
  },
  iconButton: {
    height: remCalc(32),
    padding: 0,
    width: remCalc(32),
    '&:focus': {
      outline: '#248AFF auto 1px',
    },
  },
  unpinIcon: {
    top: '-20px',
    right: '-20px',
  },
}));

export default React.memo(
  ResourceCard,
  (prevProps, nextProps) =>
    prevProps.displayCardHeader === nextProps.displayCardHeader &&
    prevProps.isPinnedResource === nextProps.isPinnedResource
);
